import { useDispatch, useSelector } from 'react-redux'
import { useLocalization } from '../../Hooks/useLocalization'
import { useEffect, useMemo, useState } from 'react'
import {
  setIsCertificateInstancesRequested,
  setIsCertificatesRequested,
} from '../../Store/DataSlice'
import { type RootState } from '../../Store/Store'
import { setDestinationView } from '../../Store/InterfaceSlice'
import './Reporting.scss'

export function Reporting() {
  const dispatch = useDispatch()
  const localization = useLocalization('reporting')

  const isCertificateInstancesRequested = useSelector(
    (state: RootState) => state.data.isCertificateInstancesRequested
  )
  useEffect(() => {
    if (!isCertificateInstancesRequested)
      dispatch(setIsCertificateInstancesRequested(true))
  }, [isCertificateInstancesRequested, dispatch])

  const allCertificateInstances = useSelector(
    (state: RootState) => state.data.certificateInstances
  )

  const isCertificatesRequested = useSelector(
    (state: RootState) => state.data.isCertificatesRequested
  )
  useEffect(() => {
    if (!isCertificatesRequested) dispatch(setIsCertificatesRequested(true))
  }, [isCertificatesRequested, dispatch])

  const allCertificates = useSelector(
    (state: RootState) => state.data.certificates
  )

  const certificateTypePerInstitution = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/consistent-indexed-object-style
    const result: {
      // eslint-disable-next-line @typescript-eslint/consistent-indexed-object-style
      [year: string]: {
        // eslint-disable-next-line @typescript-eslint/consistent-indexed-object-style
        [quarter: string]: {
          // eslint-disable-next-line @typescript-eslint/consistent-indexed-object-style
          [certificateInstanceVatNumber: string]: {
            [certType: string]: {
              total: number
              extended: number
              certUuids: string[]
            }
          }
        }
      }
    } = {}
    for (const certificate of allCertificates) {
      if (
        certificate.issueDate == null ||
        certificate.type == null ||
        certificate.certificateInstanceVatNumber == null
      )
        continue
      const issueDate = new Date(certificate.issueDate)
      const year = issueDate.getFullYear()
      const quarter = Math.floor(issueDate.getMonth() / 3) + 1
      const isExtended = certificate.extendedUntil != null
      if (result[year] == null) result[year] = {}
      if (result[year][quarter] == null) result[year][quarter] = {}
      if (
        result[year][quarter][certificate.certificateInstanceVatNumber] == null
      )
        result[year][quarter][certificate.certificateInstanceVatNumber] = {}
      if (
        result[year][quarter][certificate.certificateInstanceVatNumber][
          certificate.type
        ] == null
      )
        result[year][quarter][certificate.certificateInstanceVatNumber][
          certificate.type
        ] = {
          total: 0,
          extended: 0,
          certUuids: [],
        }
      result[year][quarter][certificate.certificateInstanceVatNumber][
        certificate.type
      ].total += 1

      result[year][quarter][certificate.certificateInstanceVatNumber][
        certificate.type
      ].certUuids.push(certificate.uuid)

      if (isExtended) {
        result[year][quarter][certificate.certificateInstanceVatNumber][
          certificate.type
        ].extended += 1
      }
    }

    return result
  }, [allCertificates])

  const [expandedCounts, setExpandedCounts] = useState<string[]>([])

  return (
    <div className="reporting">
      {allCertificateInstances.length === 0 || allCertificates.length === 0 ? (
        <div className="loading">
          {localization.text(
            'data-is-loading',
            'De data word momenteel ingeladen.'
          )}
        </div>
      ) : (
        <>
          <h1>
            {localization.text(
              'certificate-types-per-institution-title',
              'Certificaat types per instelling'
            )}
          </h1>
          {Object.keys(certificateTypePerInstitution)
            .sort()
            .reverse()
            .map((year) => (
              <ul key={year} className="year-wrapper">
                {Object.keys(certificateTypePerInstitution[year])
                  .sort()
                  .reverse()
                  .map((quarter) => (
                    <li key={quarter} className="quarter-wrapper">
                      <div className="header">
                        {year}-Q{quarter}
                      </div>

                      <ul>
                        {Object.keys(
                          certificateTypePerInstitution[year][quarter]
                        )
                          .sort()
                          .map((certificateInstanceVatNumber) => {
                            const certInstance = allCertificateInstances.find(
                              (ci) =>
                                ci.vatNumber === certificateInstanceVatNumber
                            )
                            let ciTotal = 0
                            let ciExtended = 0
                            for (const counts of Object.values(
                              certificateTypePerInstitution[year][quarter][
                                certificateInstanceVatNumber
                              ]
                            )) {
                              ciTotal += counts.total
                              ciExtended += counts.extended
                            }

                            return (
                              <li
                                key={certificateInstanceVatNumber}
                                className="cert-instance-wrapper"
                              >
                                <div className="header">
                                  <div className="cert-instance">
                                    {certInstance?.name ??
                                      certificateInstanceVatNumber}
                                  </div>
                                  <div className="total">
                                    <span className="soft-label">
                                      {localization.text(
                                        'count-total',
                                        'Totaal'
                                      )}
                                      :{' '}
                                    </span>
                                    {ciTotal}
                                  </div>
                                  <div className="extended">
                                    <span className="soft-label">
                                      {localization.text(
                                        'count-extended',
                                        'Verlengd'
                                      )}
                                      :{' '}
                                    </span>
                                    {ciExtended}
                                  </div>
                                </div>
                                <ul>
                                  {Object.keys(
                                    certificateTypePerInstitution[year][
                                      quarter
                                    ][certificateInstanceVatNumber]
                                  )
                                    .sort()
                                    .map((certType) => {
                                      const key: string = JSON.stringify({
                                        year,
                                        quarter,
                                        certificateInstanceVatNumber,
                                        certType,
                                      })
                                      const isExpanded =
                                        expandedCounts.includes(key)
                                      const counts =
                                        certificateTypePerInstitution[year][
                                          quarter
                                        ][certificateInstanceVatNumber][
                                          certType
                                        ]
                                      return (
                                        <li
                                          key={certType}
                                          className="certType-wrapper"
                                        >
                                          <div
                                            className="header"
                                            onClick={(e) => {
                                              e.stopPropagation()
                                              setExpandedCounts((previous) =>
                                                isExpanded
                                                  ? [
                                                      ...previous.filter(
                                                        (p) => p !== key
                                                      ),
                                                    ]
                                                  : [...previous, key]
                                              )
                                            }}
                                          >
                                            <div className="expansion-indicator">
                                              {isExpanded ? '▼' : '▲'}
                                            </div>
                                            <div className="cert-type">
                                              {certType}
                                            </div>
                                            <div className="total">
                                              <span className="soft-label">
                                                {localization.text(
                                                  'count-total',
                                                  'Totaal'
                                                )}
                                                :{' '}
                                              </span>
                                              {counts.total}
                                            </div>
                                            <div className="extended">
                                              <span className="soft-label">
                                                {localization.text(
                                                  'count-extended',
                                                  'Verlengd'
                                                )}
                                                :{' '}
                                              </span>
                                              {counts.extended}
                                            </div>
                                          </div>
                                          {isExpanded && (
                                            <ul>
                                              {counts.certUuids.map(
                                                (certUuid, index) => {
                                                  const certificate =
                                                    allCertificates.find(
                                                      (c) => c.uuid === certUuid
                                                    )

                                                  return (
                                                    <li
                                                      key={certUuid}
                                                      className="cert-detail-link"
                                                      onClick={(e) => {
                                                        e.stopPropagation()
                                                        dispatch(
                                                          setDestinationView({
                                                            destinationView:
                                                              'certificate-detail',
                                                            detailKey: certUuid,
                                                          })
                                                        )
                                                      }}
                                                    >
                                                      {certificate?.certificateNumber ??
                                                        index}{' '}
                                                      {certificate?.extendedUntil !=
                                                        null &&
                                                        `(${localization.text(
                                                          'count-extended',
                                                          'Verlengd'
                                                        )})`}
                                                    </li>
                                                  )
                                                }
                                              )}
                                            </ul>
                                          )}
                                        </li>
                                      )
                                    })}
                                </ul>
                              </li>
                            )
                          })}
                      </ul>
                    </li>
                  ))}
              </ul>
            ))}
        </>
      )}
    </div>
  )
}
